import {useCallback, useEffect, useRef} from "react";
import {createOfferShownLog, offerDetailKey, offerEvents, type OfferDetail, type OfferMoment} from "./offerEvents";

/**
 * Wires ONE upsell surface to the offer funnel (kitecorp/transferify-api-service#999).
 *
 * A thin adapter and nothing else: it holds this surface instance's shown-latch, names the event on
 * the domain emitter, and hands back the callback the claim CTA presses. Every rule it depends on —
 * what a moment is, when a showing counts, which moment a send belongs to — lives in the React-free
 * `./offerEvents`, and nothing here knows that a beacon exists.
 *
 * <b>The latch is per HOOK INSTANCE, which is what makes "once per surface instance" true.</b> Held in
 * a ref rather than rebuilt per render (a fresh log every render would report on every render, which
 * is the exact defect the latch exists to prevent) and deliberately not shared through the module-level
 * emitter, which outlives every mount and would suppress the genuine second showing a remount is.
 *
 * <b>It is safe against an effect that runs more than it should.</b> React 19 Strict Mode double-invokes
 * effects in development, and `occasion` arriving a moment after the offer would re-run a dependency
 * list that included it. Neither can double-count, because the dedup is the latch rather than the
 * dependency array — the property the personalization funnel had to get right by keeping every callback
 * stable, made structural here instead.
 *
 * @param moment  where this surface meets the reader; a value the api's own vocabulary admits
 * @param shown   the offer is on screen RIGHT NOW. False while the surface is mounted with nothing to
 *                show, which is the ordinary state of every one of these components
 * @param occasion the campaign key the surface is rendering, or null when unnamed or absent
 * @param detail  WHICH wall armed the send screen's upsell card and the tier it sells (ui#2555,
 *                api#1794), on the one moment that carries them; null on every other surface. A change of
 *                wall or tier is a new showing — see {@link createOfferShownLog}'s `offer` key — because the
 *                card is re-armed in place and the reader really is being sold something else.
 */
export function useOfferEvents(
	moment: OfferMoment, shown: boolean, occasion: string | null, detail: OfferDetail | null = null,
): {
	/**
	 * Report that the claim CTA was pressed. Stable across renders, so it can be passed to a memoized
	 * child without re-rendering it.
	 */
	reportClaimed: () => void;
} {
	const logRef = useRef(createOfferShownLog());
	// The surface re-renders with a fresh occasion whenever the campaign answer lands. Held in a ref and
	// refreshed so the effect below reads the LATEST one without taking it as a dependency: as a
	// dependency it would re-run the effect when a campaign key arrived after the offer did, and while
	// the latch makes that harmless, a dependency list that lies about what the effect uses is the next
	// reader's problem.
	const occasionRef = useRef(occasion);
	occasionRef.current = occasion;
	// The detail is held the same way and for the same reason, plus one of its own: the press must carry
	// the wall and tier the card is showing AT THE MOMENT IT IS PRESSED, and a stable callback cannot close over
	// it. `wallKey` is what the effect below depends on instead — the object is rebuilt on every render
	// of the card, so an object dependency would re-run the effect every render.
	const detailRef = useRef(detail);
	detailRef.current = detail;
	const wallKey = offerDetailKey(detail);

	useEffect(() => {
		if (!shown || !logRef.current.shouldReport(moment, wallKey)) {
			return;
		}
		offerEvents.shown(moment, occasionRef.current, detailRef.current ?? undefined);
	}, [moment, shown, wallKey]);

	const reportClaimed = useCallback(
		() => offerEvents.clicked(moment, occasionRef.current, detailRef.current ?? undefined), [moment]);

	return {reportClaimed};
}
